import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClassIcon from '@mui/icons-material/Class';
import SchoolIcon from '@mui/icons-material/School';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';

const ClassListCard = ({ orgId }) => {
  const { axios, token } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    let isMounted = true;
    const fetchClasses = async () => {
      if (!orgId) return;
      try {
        setLoading(true);
        const res = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const rawItems = res.data?.items || res.data || [];
        const formatted = rawItems.map(c => {
          const cName = c.className || c.name || 'Class';
          const sName = c.sectionName || '';
          let title = cName;
          if (sName && sName.toLowerCase() !== cName.toLowerCase()) {
            title = `${cName} (${sName})`;
          }
          return {
            id: c.classId || c.id,
            displayName: title,
          };
        });
        if (isMounted) setClasses(formatted);
      } catch (err) {
        console.warn('Failed to load classes for ClassListCard, trying fallback endpoint...', err);
        try {
          const fallbackRes = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const rawItems = fallbackRes.data?.items || fallbackRes.data || [];
          const formatted = rawItems.map(c => {
            const cName = c.className || c.name || 'Class';
            const sName = c.sectionName || '';
            let title = cName;
            if (sName && sName.toLowerCase() !== cName.toLowerCase()) {
              title = `${cName} (${sName})`;
            }
            return {
              id: c.classId || c.id,
              displayName: title,
            };
          });
          if (isMounted) setClasses(formatted);
        } catch (e2) {
          console.error('Error fetching classes:', e2);
          if (isMounted) setClasses([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchClasses();
    return () => { isMounted = false; };
  }, [orgId, token, axios]);

  const filteredClasses = classes.filter(c =>
    c.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="dashboard-card class-performance-panel" style={{ padding: '20px' }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <SchoolIcon style={{ color: '#014486' }} />
          <h3 className="card-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Classes & Sections
          </h3>
          <Chip
            label={`${classes.length} Active`}
            size="small"
            style={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem' }}
          />
        </div>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/classes')}
          style={{
            backgroundColor: '#014486',
            textTransform: 'none',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 600,
            boxShadow: 'none'
          }}
        >
          Manage Classes
        </Button>
      </div>

      {/* Search Input */}
      <Box style={{ marginBottom: '16px' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search classes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: '#94a3b8', fontSize: '18px' }} />
              </InputAdornment>
            ),
            style: { borderRadius: '8px', backgroundColor: '#f8fafc', fontSize: '0.85rem' }
          }}
        />
      </Box>

      {/* Classes List Container */}
      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
          <CircularProgress size={28} style={{ color: '#014486' }} />
        </Box>
      ) : filteredClasses.length === 0 ? (
        <Box style={{ textAlign: 'center', padding: '30px 10px', color: '#64748b' }}>
          <ClassIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 8 }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {searchQuery ? 'No matching classes found' : 'No classes created yet'}
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/classes')}
            style={{ color: '#014486', textTransform: 'none', marginTop: 8 }}
          >
            + Create your first class
          </Button>
        </Box>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' }}>
          {filteredClasses.map((item, idx) => (
            <div
              key={item.id || idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '10px',
                backgroundColor: '#ffffff',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/students', { state: { selectedClassId: item.id } })}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#014486';
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.backgroundColor = '#ffffff';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '8px',
                  backgroundColor: '#eff6ff',
                  color: '#014486',
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'center',
                  fontWeight: 700,
                  fontSize: '0.9rem'
                }}>
                  <ClassIcon style={{ fontSize: '20px' }} />
                </div>
                <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
                  {item.displayName}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClassListCard;
